import {
    CSSProperties,
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import {
    IVideoSliderItemProps,
    IVideoSliderProps,
    IVideoStageProps
} from "./VideoSlider.interface";
import { TranslationContext } from "../../../shared/providers/translation/translation.provider";
import { ModuleContext } from "../../../shared/providers/module/module.provider";
import { CONTENT_BASE_URL } from "../../../shared/constants";
import { ComponentTypes } from "../../../shared/interfaces/modules.interface";
import Placeholder from "../../../shared/assets/imgs/placeholder.svg";
import Player, { TimeEvent } from "@vimeo/player";
import Button from "../../Button/Button";
import { useIntersectionObserver } from "../../../shared/hooks/useIntersectionObserver";
import { useDrag } from "../../../shared/hooks/useDrag";
import { LayoutContext } from "../../../shared/providers/layout/layout.provider";

function VideoSlider(props: IVideoSliderProps) {
    const { lang } = useContext(TranslationContext);
    const slider = useRef<HTMLDivElement>(null);
    const [currentVideo, setCurrentVideo] = useState<number>(0);
    const [visibleSlide, setVisibleSlide] = useState<number>(0);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [completedVideos, setCompletedVideos] = useState<string[]>([]);
    const [videosProgress, setVideosProgress] = useState<{ [index: string]: number }>();
    const { setProgress } = useContext(ModuleContext);

    const parseId = useCallback((url: string) => {
        const params = url.split("/").pop();
        const [id] = params?.split("#t=") ?? [];
        return id;
    }, []);

    useEffect(() => {
        if (lang && completedVideos.length >= props.videos.length)
            setProgress({
                unitId: props.unitId,
                itemId: props.id,
                progress: 100,
                itemType: ComponentTypes.VIDEO_SLIDER,
                itemLocale: lang
            });
    }, [lang, completedVideos]);

    useEffect(() => {
        if (!videosProgress?.[currentVideo] || videosProgress?.[currentVideo] < 1) return;

        const nextIndex = currentVideo + 1;
        if (nextIndex >= props.videos.length) {
            setIsFullscreen(false);
        } else {
            setCurrentVideo(nextIndex);
        }
    }, [videosProgress]);

    const handleDrag = (delta: number) => {
        if (!stage.current) return;
        (stage.current as HTMLElement).scrollBy(delta, 0);
    };

    const { ref: stage } = useDrag({
        onDragLeft: handleDrag,
        onDragRight: handleDrag
    });

    return (
        <section className="unit_item no_margins container-fluid gx-2 py-2 pe-0">
            <div className="row gx-0">
                <div className="col-12">
                    <h2 className="text-h4-bd mb-1">{props.title}</h2>
                    <p className="text-h3-rg color-neutrals-darker mb-12">{props.content}</p>
                    <div className="slider_video" ref={slider}>
                        <div className="slider_video-stage" ref={stage}>
                            {props.videos.map((video, i) => (
                                <VideoItem
                                    key={i}
                                    {...props}
                                    index={i}
                                    setCurrentVideo={setCurrentVideo}
                                    setIsFullscreen={setIsFullscreen}
                                    videoId={parseId(video.videoUrl)}
                                    thumbnail={video.thumbnail}
                                    setVisibleSlide={setVisibleSlide}
                                />
                            ))}
                        </div>
                        <div className="d-flex gap-1 align-items-center">
                            <div className="slider_image-navigation">
                                {props.videos.map((_, i) => {
                                    if (i === 0) return <Fragment key={i} />;
                                    return (
                                        <span
                                            data-index={i}
                                            key={i}
                                            className={`navigation_item ${i === visibleSlide ? "is_active" : ""}`}></span>
                                    );
                                })}
                            </div>
                            <div className="ms-auto d-flex gap-02">
                                <Button
                                    type="button"
                                    icon="Chevron--left"
                                    iconSize={22}
                                    variant="solid"
                                    customCss="rounded-circle color-neutrals-white p-01"
                                    onClick={() => {
                                        if (!stage.current) return;
                                        const stageEl = stage.current as HTMLElement;
                                        stageEl.scrollBy(-50, 0);
                                    }}
                                />
                                <Button
                                    type="button"
                                    icon="Chevron--right"
                                    iconSize={22}
                                    variant="solid"
                                    customCss="rounded-circle color-neutrals-white p-01"
                                    onClick={() => {
                                        if (!stage.current) return;
                                        const stageEl = stage.current as HTMLElement;
                                        stageEl.scrollBy(50, 0);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {isFullscreen && (
                <VideoStage
                    title={props.title}
                    videos={props.videos}
                    videoId={parseId(props.videos[currentVideo].videoUrl)}
                    setCurrentVideo={setCurrentVideo}
                    index={currentVideo}
                    setCompletedVideos={setCompletedVideos}
                    setVideosProgress={setVideosProgress}
                    setIsFullscreen={setIsFullscreen}
                />
            )}
        </section>
    );
}

export default VideoSlider;

const VideoItem = (props: IVideoSliderItemProps) => {
    const { index, thumbnail, videoId, setVisibleSlide, setCurrentVideo, setIsFullscreen } = props;
    const { translate } = useContext(TranslationContext);

    const handleIntersection = async (e: any) => {
        if (!e.isIntersecting) return;

        setVisibleSlide(index);
    };

    const handlePlay = useCallback(() => {
        setCurrentVideo(index);
        setIsFullscreen(true);
    }, [setCurrentVideo, index]);

    const { ref: thumbnailRef } = useIntersectionObserver(handleIntersection);

    if (!videoId) return <></>;

    return (
        <div className="video_wrapper">
            <button type="button" onClick={handlePlay}>
                <img
                    ref={thumbnailRef}
                    className="video_thumb"
                    src={thumbnail ? `${CONTENT_BASE_URL}${thumbnail.url}` : Placeholder}
                    aria-label={translate("common.play", "Play")}
                />
            </button>
        </div>
    );
};

const VideoStage = (props: IVideoStageProps) => {
    const {
        videoId,
        title,
        videos,
        setIsFullscreen,
        setCurrentVideo,
        index,
        setVideosProgress,
        setCompletedVideos
    } = props;
    const { translate } = useContext(TranslationContext);
    const ref = useRef<HTMLIFrameElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const { header, main } = useContext(LayoutContext);
    const [isCompleted, setIsCompleted] = useState<boolean>(false);
    const [percentage, setPercentage] = useState<number>(0);

    useEffect(() => {
        stageRef.current?.style.setProperty(
            "--mt",
            `${header.current?.getBoundingClientRect().height}px`
        );
        window.scrollTo(0, 0);
        document.body.classList.add("overflow-hidden");
        return () => document.body.classList.remove("overflow-hidden");
    }, [header, main]);

    useEffect(() => {
        if (!ref.current) return;

        const player = new Player(ref.current);
        player.loadVideo(videoId).then((_) => {
            if (!ref.current) return;
            const player = new Player(ref.current);

            player.play();
            player.on("timeupdate", (e: TimeEvent) => {
                setPercentage(Math.round(e.percent * 100));
                setIsCompleted(e.percent >= 0.9);
                setVideosProgress((p: any) => ({ ...p, [index]: e.percent }));
            });
        });
    }, [index, ref]);

    useEffect(() => {
        if (isCompleted) {
            setCompletedVideos((v: string) => [...v, videoId]);
        }
    }, [isCompleted]);

    const handlePrevious = () => {
        if (videos[index - 1]) setCurrentVideo(index - 1);
    };

    const handleNext = () => {
        if (videos[index + 1]) setCurrentVideo(index + 1);
    };

    if (!videoId) return <></>;

    return (
        <div className="fullscreen_video" ref={stageRef}>
            <Button
                type="button"
                label={translate("common.back", "Back")}
                icon="Chevron--left"
                iconSize={20}
                onClick={() => setIsFullscreen(false)}
                customCss="btn_back d-none d-md-flex align-items-center gap-01 color-neutrals-black text-bd2-rg mt-n1 ms-n1"
            />
            <Button
                type="button"
                icon="Close"
                iconSize={20}
                onClick={() => setIsFullscreen(false)}
                customCss="d-md-none btn_close color-neutrals-black"
            />
            <div className="video_wrapper">
                <h1 className="text-h2-bd mb-11">{title}</h1>
                <div className="d-flex gap-13">
                    <div className="d-flex gap-02 flex-column w-100">
                        {
                            <iframe
                                ref={ref}
                                title={title}
                                src={`https://player.vimeo.com/video/${videoId}`}
                                width="640"
                                height="360"
                                className="item_video"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen></iframe>
                        }
                        <div className="card d-flex gap-02 align-items-center">
                            <progress
                                className="card_progress"
                                value={percentage}
                                max="100"></progress>
                            <span className="text-bd4-md color-neutrals-neutral">
                                {percentage}%
                            </span>
                        </div>
                    </div>
                    <div className="navigation d-flex flex-column gap-02 justify-content-center">
                        <Button
                            disabled={!videos[index - 1]}
                            type="button"
                            icon="Chevron--up"
                            iconSize={24}
                            onClick={handlePrevious}
                            variant="solid"
                            customCss="rounded-circle color-neutrals-white p-01"
                        />
                        <Button
                            disabled={!videos[index + 1]}
                            type="button"
                            icon="Chevron--down"
                            iconSize={24}
                            onClick={handleNext}
                            variant="solid"
                            customCss="rounded-circle color-neutrals-white p-01"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
